import React, { useEffect, useMemo, useRef, useState } from "react";
import { formatDistanceToNow, parseISO } from "date-fns";
import { useLocation, useNavigate } from "react-router-dom";
import allApi from "../../../api/allApi";
import ubr from "../../../assets/images/ubr-2.png";
import ap from "../../../assets/images/ap-2.png";
import switchImg from "../../../assets/images/switch.png";
import Loader from "../common/Loader";
import NmsTableSearch, { searchRows } from "../common/NmsTableSearch";
import { useLocalization } from "../localization/LocalizationContext";
import { useNetwork } from "../common/networkContext";

const PING_DEFAULTS = { count: "4", size: "46", timeout: "5", interval: "1" };
const TRACE_DEFAULTS = {
  count: "4",
  maxTTL: "30",
  initTTL: "1",
  interval: "1",
  port: "33434",
  size: "46",
  host: "",
};

const resultText = (value) => {
  if (value == null || value === "") return "";
  if (Array.isArray(value)) return value.map(resultText).filter(Boolean).join("\n");
  if (typeof value === "object") {
    if (value.response_data != null) return resultText(value.response_data);
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

const statusCode = (status) => {
  if (status == null || status === "") return null;
  const code = Number(status);
  return Number.isNaN(code) ? null : code;
};

const relativeTime = (timestamp) => {
  if (!timestamp) return "";
  try {
    return formatDistanceToNow(parseISO(timestamp), { addSuffix: true });
  } catch {
    return "";
  }
};

const deviceImage = (deviceType) => {
  if (deviceType === "p2p") return ubr;
  if (deviceType === "AP") return ap;
  return switchImg;
};

const wait = (ms, timerRef, cancelledRef) =>
  new Promise((resolve, reject) => {
    timerRef.current = window.setTimeout(() => {
      if (cancelledRef.current) reject(new Error("cancelled"));
      else resolve();
    }, ms);
  });

const StatusChip = ({ kind, label, stamp, onClick }) => (
  <button
    type="button"
    className={`ts-status ${kind}`}
    onClick={onClick}
    title={stamp || label}
  >
    <strong>{label}</strong>
    {stamp ? <span>{stamp}</span> : null}
  </button>
);

function Troubleshoot() {
  const { localizationStrings } = useLocalization();
  const { selectedNetworkId } = useNetwork();
  const networkId = selectedNetworkId?.id ?? 6001;
  const navigate = useNavigate();
  const location = useLocation();
  const deviceIdParam = new URLSearchParams(location.search).get("deviceId");

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [devices, setDevices] = useState([]);
  const [tableQuery, setTableQuery] = useState("");
  const [healthFilter, setHealthFilter] = useState("all");
  const [selectedSerial, setSelectedSerial] = useState("");
  const [license, setLicense] = useState(
    () => (typeof window !== "undefined" && localStorage.getItem("license")) || ""
  );
  const [testType, setTestType] = useState("ping");
  const [ipAddress, setIpAddress] = useState("");
  const [dnsDomain, setDnsDomain] = useState("");
  const [pingParams, setPingParams] = useState(PING_DEFAULTS);
  const [traceParams, setTraceParams] = useState(TRACE_DEFAULTS);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [pingErrors, setPingErrors] = useState({});
  const [traceErrors, setTraceErrors] = useState({});
  const [consoleText, setConsoleText] = useState("");
  const [consoleTitle, setConsoleTitle] = useState("");
  const [runState, setRunState] = useState("idle");
  const [openingDevice, setOpeningDevice] = useState(false);

  const pollTimerRef = useRef(null);
  const cancelledRef = useRef(false);
  const showPremium = license !== "Standard";

  useEffect(() => {
    if (!showPremium && testType !== "ping") setTestType("ping");
  }, [showPremium, testType]);

  useEffect(() => () => {
    cancelledRef.current = true;
    if (pollTimerRef.current) window.clearTimeout(pollTimerRef.current);
  }, []);

  const labelFor = (test) => {
    if (!test) return localizationStrings?.notAvailable || "Not Available";
    const code = statusCode(test.status);
    if (code === 0) return localizationStrings?.success || "Success";
    if (code === 1) return localizationStrings?.pending || "Pending";
    if (code === 2) return localizationStrings?.failure || "Failure";
    return localizationStrings?.notAvailable || "Not Available";
  };

  const kindFor = (label) => {
    const value = String(label || "").toLowerCase();
    if (value.includes("success")) return "success";
    if (value.includes("fail")) return "failure";
    if (value.includes("pending")) return "pending";
    return "muted";
  };

  const enrichDevices = (list, troubleshootData) =>
    (list || []).map((device) => {
      const match = (troubleshootData || []).find(
        (item) => item.serial_no === device.serialNumber
      );
      const dnsLookup = match?.dns_lookup;
      const pingTest = match?.ping_test;
      const traceRoute = match?.trace_route;
      return {
        ...device,
        dnsTimestamp: relativeTime(dnsLookup?.timestamp),
        pingTimestamp: relativeTime(pingTest?.timestamp),
        traceTimestamp: relativeTime(traceRoute?.timestamp),
        dnsStatus: labelFor(dnsLookup),
        pingStatus: labelFor(pingTest),
        traceRouteStatus: labelFor(traceRoute),
      };
    });

  const loadDevices = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      setRefreshing(true);
      const api = new allApi();
      const devicesResponse = await api.getRequest(`api/devices/http/${networkId}`);
      const list = Array.isArray(devicesResponse) ? devicesResponse : [];
      let troubleshootData = [];
      try {
        const troubleshootResponse = await api.getRequest(
          `api/troubleshoot/response/all?networkId=${networkId}`
        );
        if (
          troubleshootResponse?.status !== "404" &&
          Array.isArray(troubleshootResponse?.data)
        ) {
          troubleshootData = troubleshootResponse.data;
        } else if (Array.isArray(troubleshootResponse)) {
          troubleshootData = troubleshootResponse;
        }
      } catch {
        troubleshootData = [];
      }

      const incoming = location.state?.deviceTroubleshoot;
      const incomingDevice = Array.isArray(incoming) ? incoming[0] : incoming;
      const merged = [...list];
      if (
        incomingDevice?.serialNumber &&
        !merged.some((device) => device.serialNumber === incomingDevice.serialNumber)
      ) {
        merged.unshift(incomingDevice);
      }

      const enriched = enrichDevices(merged, troubleshootData);
      setDevices(enriched);

      const fromQuery = enriched.find(
        (device) => String(device.deviceId) === String(deviceIdParam)
      )?.serialNumber;
      const fallback = incomingDevice?.serialNumber || fromQuery;

      setSelectedSerial((current) => {
        if (current && enriched.some((device) => device.serialNumber === current)) {
          return current;
        }
        if (fallback && enriched.some((device) => device.serialNumber === fallback)) {
          return fallback;
        }
        const firstOnline = enriched.find((device) => device.health === "Online");
        return firstOnline?.serialNumber || current;
      });
    } catch (error) {
      console.error("Error fetching device data:", error);
      setDevices([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkId, location.state, deviceIdParam]);

  const visibleDevices = useMemo(() => {
    const searched = searchRows(devices, tableQuery, [
      "deviceName",
      "serialNumber",
      "model",
      "macAddress",
      "health",
      "ipAddress",
    ]);
    if (healthFilter === "all") return searched;
    return searched.filter(
      (device) => String(device.health || "").toLowerCase() === healthFilter
    );
  }, [devices, tableQuery, healthFilter]);

  const selectedDevice =
    devices.find((device) => device.serialNumber === selectedSerial) || null;
  const isOnline = selectedDevice?.health === "Online";
  const isRunning = runState === "running";

  const counts = useMemo(
    () => ({
      total: devices.length,
      online: devices.filter((device) => device.health === "Online").length,
      offline: devices.filter((device) => device.health === "Offline").length,
      awaiting: devices.filter((device) => device.health === "Awaiting").length,
    }),
    [devices]
  );

  const stopPolling = () => {
    cancelledRef.current = true;
    if (pollTimerRef.current) {
      window.clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const pollResult = async (serial, uuid, intervalMs, maxNulls) => {
    cancelledRef.current = false;
    let nulls = 0;
    const api = new allApi();
    while (!cancelledRef.current) {
      const statusResponse = await api.getRequest(
        `api/troubleshoot/response/${serial}/${uuid}`
      );
      const code = statusCode(statusResponse?.status);
      if (code === 0) return { ok: true, data: statusResponse };
      if (code === 2) return { ok: false, data: statusResponse };
      if (code === 1) nulls = 0;
      else {
        nulls += 1;
        if (nulls >= maxNulls) {
          return { ok: false, timedOut: true, data: statusResponse };
        }
      }
      await wait(intervalMs, pollTimerRef, cancelledRef);
    }
    return { ok: false, cancelled: true };
  };

  const loadLastResult = async (serial, type = testType) => {
    if (!serial) return;
    const path =
      type === "dns"
        ? "dns-lookup"
        : type === "traceroute"
          ? "traceroute"
          : "ping";
    const titles = {
      ping: localizationStrings?.pingTestResult || "Ping test result",
      dns: localizationStrings?.DNSTestResult || "DNS test result",
      traceroute:
        localizationStrings?.tracerouteTestResult || "Traceroute test result",
    };
    try {
      const api = new allApi();
      const response = await api.getRequest(
        `api/troubleshoot/testResponse/${path}/${serial}`
      );
      const text = resultText(response?.response_data ?? response);
      setConsoleTitle(titles[type]);
      setConsoleText(
        text || localizationStrings?.noData || "No previous result for this test."
      );
      setRunState("idle");
    } catch {
      setConsoleTitle(titles[type]);
      setConsoleText(
        localizationStrings?.notAvailable || "No previous result for this test."
      );
      setRunState("idle");
    }
  };

  useEffect(() => {
    if (!selectedSerial || isRunning) return;
    loadLastResult(selectedSerial, testType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSerial, testType]);

  const selectDevice = (device, nextTest) => {
    if (!device || isRunning) return;
    setSelectedSerial(device.serialNumber);
    if (nextTest) setTestType(nextTest);
  };

  const validatePing = (params) => {
    const next = {};
    const count = Number(params.count);
    const size = Number(params.size);
    const timeout = Number(params.timeout);
    const interval = Number(params.interval);
    if (!(count >= 1 && count <= 1024)) next.count = "Value must be between 1 and 1024";
    if (!(size >= 46 && size <= 1024)) next.size = "Value must be between 46 and 1024";
    if (!(timeout >= 1 && timeout <= 300)) next.timeout = "Value must be between 1 and 300";
    if (!(interval >= 1 && interval <= 10)) next.interval = "Value must be between 1 and 10";
    setPingErrors(next);
    return Object.keys(next).length === 0;
  };

  const validateTrace = (params) => {
    const next = {};
    if (!(Number(params.count) >= 1 && Number(params.count) <= 10)) {
      next.count = "Count must be between 1 and 10.";
    }
    if (!(Number(params.maxTTL) >= 1 && Number(params.maxTTL) <= 255)) {
      next.maxTTL = "Max TTL must be between 1 and 255.";
    }
    if (!(Number(params.initTTL) >= 1 && Number(params.initTTL) <= 30)) {
      next.initTTL = "Init TTL must be between 1 and 30.";
    }
    if (!(Number(params.interval) >= 1 && Number(params.interval) <= 60)) {
      next.interval = "Interval must be between 1 and 60.";
    }
    if (!(Number(params.port) >= 1 && Number(params.port) <= 65535)) {
      next.port = "Port must be between 1 and 65535.";
    }
    if (!(Number(params.size) >= 46 && Number(params.size) <= 1024)) {
      next.size = "Size must be between 46 and 1024.";
    }
    setTraceErrors(next);
    return Object.keys(next).length === 0;
  };

  const finishRun = async (ok, data, failureLabel) => {
    const output = resultText(data?.response_data ?? data);
    setRunState(ok ? "success" : "failure");
    setConsoleText(
      output ||
        (ok
          ? localizationStrings?.success || "Success"
          : failureLabel)
    );
    await loadDevices({ silent: true });
  };

  const runPing = async () => {
    if (!selectedDevice || !ipAddress.trim() || !validatePing(pingParams)) return;
    stopPolling();
    setRunState("running");
    setConsoleTitle(localizationStrings?.pingTest || "Ping Test");
    setConsoleText(localizationStrings?.pending || "Waiting for the device to respond…");
    try {
      const api = new allApi();
      const initialResponse = await api.postRequest("api/troubleshoot/ping", "", {
        SerialNumber: selectedDevice.serialNumber,
        type: 67,
        ipVersion: 4,
        host: ipAddress.trim(),
        action: 1,
        count: pingParams.count,
        size: pingParams.size,
        timeout: pingParams.timeout,
        interval: pingParams.interval,
      });
      const uuid = initialResponse?.uuid;
      if (!uuid) {
        setRunState("failure");
        setConsoleText(localizationStrings?.pingFailed || "The Ping test failed");
        return;
      }
      const result = await pollResult(selectedDevice.serialNumber, uuid, 1000, 50);
      if (result.cancelled) return;
      await finishRun(
        result.ok,
        result.data,
        localizationStrings?.pingFailed || "The Ping test failed"
      );
    } catch (error) {
      if (error?.message === "cancelled") return;
      setRunState("failure");
      setConsoleText(localizationStrings?.pingFailed || "The Ping test failed");
    }
  };

  const runTraceroute = async () => {
    if (!selectedDevice || !traceParams.host.trim() || !validateTrace(traceParams)) {
      return;
    }
    stopPolling();
    setRunState("running");
    setConsoleTitle(localizationStrings?.traceroute || "Traceroute");
    setConsoleText(localizationStrings?.pending || "Waiting for the device to respond…");
    try {
      const api = new allApi();
      const initialResponse = await api.postRequest("api/troubleshoot/traceroute", "", {
        SerialNumber: selectedDevice.serialNumber,
        wait: 3,
        ipVersion: 4,
        size: parseInt(traceParams.size, 10),
        port: parseInt(traceParams.port, 10),
        maxTTL: parseInt(traceParams.maxTTL, 10),
        host: traceParams.host.trim(),
        action: 1,
        initTTL: parseInt(traceParams.initTTL, 10),
        nQueries: parseInt(traceParams.count, 10),
        type: 68,
      });
      const uuid = initialResponse?.uuid;
      if (!uuid) {
        setRunState("failure");
        setConsoleText(localizationStrings?.traceFailed || "The Traceroute test failed");
        return;
      }
      const result = await pollResult(selectedDevice.serialNumber, uuid, 2000, 50);
      if (result.cancelled) return;
      await finishRun(
        result.ok,
        result.data,
        localizationStrings?.traceFailed || "The Traceroute test failed"
      );
    } catch (error) {
      if (error?.message === "cancelled") return;
      setRunState("failure");
      setConsoleText(localizationStrings?.traceFailed || "The Traceroute test failed");
    }
  };

  const runDns = async () => {
    if (!selectedDevice || !dnsDomain.trim()) return;
    stopPolling();
    setRunState("running");
    setConsoleTitle(localizationStrings?.dnsLookup || "DNS Lookup");
    setConsoleText(localizationStrings?.pending || "Waiting for the device to respond…");
    try {
      const api = new allApi();
      const response = await api.postRequest("api/troubleshoot/dnsLookup", "", {
        SerialNumber: selectedDevice.serialNumber,
        domainName: dnsDomain.trim(),
      });
      const uuid = response?.uuid;
      if (!uuid) {
        setRunState("failure");
        setConsoleText(localizationStrings?.lookupFailed || "The DNS lookup test failed");
        return;
      }
      const result = await pollResult(selectedDevice.serialNumber, uuid, 3000, 25);
      if (result.cancelled) return;
      await finishRun(
        result.ok,
        result.data,
        localizationStrings?.lookupFailed || "The DNS lookup test failed"
      );
    } catch (error) {
      if (error?.message === "cancelled") return;
      setRunState("failure");
      setConsoleText(localizationStrings?.lookupFailed || "The DNS lookup test failed");
    }
  };

  const handleRun = (event) => {
    event.preventDefault();
    if (!isOnline || isRunning) return;
    if (testType === "dns") return runDns();
    if (testType === "traceroute") return runTraceroute();
    return runPing();
  };

  const openDevice = async (device) => {
    if (!device?.deviceId) return;
    try {
      setOpeningDevice(true);
      const api = new allApi();
      const response = await api.getRequest(
        `api/device?protocolType=http&deviceId=${device.deviceId}`
      );
      if (response) {
        navigate("/https-info", { state: { deviceInfo: response } });
      }
    } catch (error) {
      console.error("Error opening device:", error);
    } finally {
      setOpeningDevice(false);
    }
  };

  const canRun =
    isOnline &&
    !isRunning &&
    ((testType === "ping" && ipAddress.trim() && !Object.keys(pingErrors).length) ||
      (testType === "dns" && dnsDomain.trim()) ||
      (testType === "traceroute" &&
        traceParams.host.trim() &&
        !Object.keys(traceErrors).length));

  const healthChips = [
    { id: "all", label: localizationStrings?.all || "All", count: counts.total },
    { id: "online", label: localizationStrings?.online || "Online", count: counts.online },
    {
      id: "offline",
      label: localizationStrings?.offline || "Offline",
      count: counts.offline,
    },
    {
      id: "awaiting",
      label: localizationStrings?.awaiting || "Awaiting",
      count: counts.awaiting,
    },
  ];

  const tests = [
    {
      id: "ping",
      label: localizationStrings?.pingTest || "Ping Test",
      hint: localizationStrings?.packetInfo || localizationStrings?.pingTestInfo,
    },
    ...(showPremium
      ? [
          {
            id: "dns",
            label: localizationStrings?.dnsLookup || "DNS Lookup",
            hint: localizationStrings?.dnsDomainInfo || localizationStrings?.dnsTestInfo,
          },
          {
            id: "traceroute",
            label: localizationStrings?.traceroute || "Traceroute",
            hint:
              localizationStrings?.packetTripInfo ||
              localizationStrings?.tracerouteTestInfo,
          },
        ]
      : []),
  ];

  return (
    <div className="nms-dash">
      <div className="ts-layout">
        <div className="inc-card ch-100">
          <div className="inc-card-title">
            <h3>
              {localizationStrings?.troubleshoot}
              <span className="ts-count">{counts.total}</span>
            </h3>
            <div className="inc-card-button">
              <div className="ts-chips">
                {healthChips.map((chip) => (
                  <button
                    key={chip.id}
                    type="button"
                    className={`ts-chip${healthFilter === chip.id ? " is-active" : ""}`}
                    onClick={() => setHealthFilter(chip.id)}
                  >
                    {chip.label} {chip.count}
                  </button>
                ))}
              </div>
              <NmsTableSearch
                value={tableQuery}
                onChange={setTableQuery}
                placeholder={localizationStrings?.search || "Search devices"}
              />
              <button
                className="icon-btn"
                title="Refresh"
                onClick={() => loadDevices()}
              >
                {refreshing ? (
                  <i className="fa fa-spinner fa-spin" aria-hidden="true"></i>
                ) : (
                  <i className="fa fa-refresh" aria-hidden="true"></i>
                )}
              </button>
            </div>
          </div>
          <div className="inc-card-body">
            {loading ? (
              <Loader />
            ) : (
              <div className="inc-card-table">
                <table className="full-table m-head">
                  <thead>
                    <tr>
                      <th colSpan={2}>{localizationStrings?.deviceInformation}</th>
                      <th>{localizationStrings?.pingTest}</th>
                      {showPremium && <th>{localizationStrings?.dnsLookup}</th>}
                      {showPremium && <th>{localizationStrings?.traceroute}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleDevices.length > 0 ? (
                      visibleDevices.map((device) => (
                        <tr
                          key={device.serialNumber || device.macAddress}
                          className={`ts-device-row${
                            selectedSerial === device.serialNumber ? " is-selected" : ""
                          }`}
                          onClick={() => selectDevice(device)}
                        >
                          <td className="t-device">
                            <img src={deviceImage(device.deviceType)} alt="" />
                          </td>
                          <td>
                            <div className="t-device-info">
                              <p>
                                <strong>{device.deviceName || "Unnamed device"}</strong>
                              </p>
                              <p>
                                {localizationStrings?.serialNumber}: {device.serialNumber}
                              </p>
                              <p>
                                {localizationStrings?.model}: {device.model || "N/A"}
                              </p>
                              <p>
                                {localizationStrings?.macAddress}: {device.macAddress}
                              </p>
                              <p>
                                <span
                                  className={
                                    device.health === "Online"
                                      ? "text-success"
                                      : device.health === "Offline"
                                        ? "text-danger"
                                        : "text-warning"
                                  }
                                >
                                  {device.health}
                                </span>
                              </p>
                            </div>
                          </td>
                          <td>
                            <StatusChip
                              kind={kindFor(device.pingStatus)}
                              label={device.pingStatus}
                              stamp={device.pingTimestamp}
                              onClick={(event) => {
                                event.stopPropagation();
                                selectDevice(device, "ping");
                              }}
                            />
                          </td>
                          {showPremium && (
                            <td>
                              <StatusChip
                                kind={kindFor(device.dnsStatus)}
                                label={device.dnsStatus}
                                stamp={device.dnsTimestamp}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  selectDevice(device, "dns");
                                }}
                              />
                            </td>
                          )}
                          {showPremium && (
                            <td>
                              <StatusChip
                                kind={kindFor(device.traceRouteStatus)}
                                label={device.traceRouteStatus}
                                stamp={device.traceTimestamp}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  selectDevice(device, "traceroute");
                                }}
                              />
                            </td>
                          )}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={showPremium ? 5 : 3}>
                          <div className="nms-empty">
                            {localizationStrings?.noData}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="inc-card ts-console-card">
          <div className="inc-card-title">
            <h3>{localizationStrings?.test || "Diagnostics"}</h3>
          </div>
          <div className="inc-card-body">
            {!selectedDevice ? (
              <div className="nms-empty">
                Select a device to run ping, DNS, or traceroute.
              </div>
            ) : (
              <>
                <div className="ts-device-summary">
                  <div>
                    <strong>{selectedDevice.deviceName || selectedDevice.serialNumber}</strong>
                    <p>
                      {selectedDevice.serialNumber}
                      {selectedDevice.ipAddress ? ` · ${selectedDevice.ipAddress}` : ""}
                    </p>
                  </div>
                  {selectedDevice.deviceId ? (
                    <button
                      type="button"
                      className="nms-link-btn"
                      disabled={openingDevice}
                      onClick={() => openDevice(selectedDevice)}
                    >
                      {localizationStrings?.deviceConfiguration}
                    </button>
                  ) : null}
                </div>

                {!isOnline && (
                  <p className="ts-offline-note">
                    {selectedDevice.health === "Offline"
                      ? "This device is offline. You can view the last result, but tests can only run on online devices."
                      : "This device is awaiting connection. Tests can only run on online devices."}
                  </p>
                )}

                <div className="ts-tabs" role="tablist">
                  {tests.map((test) => (
                    <button
                      key={test.id}
                      type="button"
                      className={`ts-tab${testType === test.id ? " is-active" : ""}`}
                      onClick={() => setTestType(test.id)}
                      disabled={isRunning}
                    >
                      {test.label}
                    </button>
                  ))}
                </div>
                <p className="ts-hint">
                  {tests.find((test) => test.id === testType)?.hint}
                </p>

                <form className="ts-form" onSubmit={handleRun}>
                  {testType === "ping" && (
                    <>
                      <label className="col-form-label">
                        {localizationStrings?.ipAddress} {localizationStrings?.or}{" "}
                        {localizationStrings?.hostName}
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={ipAddress}
                        onChange={(event) => setIpAddress(event.target.value)}
                        placeholder="8.8.8.8 or example.com"
                        disabled={isRunning}
                      />
                      <p className="ts-advanced-toggle">
                        {localizationStrings?.pingDefault}
                        {" · "}
                        <button
                          type="button"
                          onClick={() => setShowAdvanced((open) => !open)}
                        >
                          {localizationStrings?.advancedSettings}
                        </button>
                      </p>
                      {showAdvanced && (
                        <div className="ts-form-grid">
                          <div>
                            <label className="col-form-label">
                              {localizationStrings?.pingCount} (1–1024)
                            </label>
                            <input
                              className="form-control"
                              value={pingParams.count}
                              onChange={(event) =>
                                setPingParams((prev) => ({
                                  ...prev,
                                  count: event.target.value,
                                }))
                              }
                            />
                            {pingErrors.count && (
                              <span className="required-message">{pingErrors.count}</span>
                            )}
                          </div>
                          <div>
                            <label className="col-form-label">
                              {localizationStrings?.packetSize} (46–1024 {localizationStrings?.bytes})
                            </label>
                            <input
                              className="form-control"
                              value={pingParams.size}
                              onChange={(event) =>
                                setPingParams((prev) => ({
                                  ...prev,
                                  size: event.target.value,
                                }))
                              }
                            />
                            {pingErrors.size && (
                              <span className="required-message">{pingErrors.size}</span>
                            )}
                          </div>
                          <div>
                            <label className="col-form-label">
                              {localizationStrings?.pingTimeout} (1–300)
                            </label>
                            <input
                              className="form-control"
                              value={pingParams.timeout}
                              onChange={(event) =>
                                setPingParams((prev) => ({
                                  ...prev,
                                  timeout: event.target.value,
                                }))
                              }
                            />
                            {pingErrors.timeout && (
                              <span className="required-message">{pingErrors.timeout}</span>
                            )}
                          </div>
                          <div>
                            <label className="col-form-label">
                              {localizationStrings?.pingInterval} (1–10 seconds)
                            </label>
                            <input
                              className="form-control"
                              value={pingParams.interval}
                              onChange={(event) =>
                                setPingParams((prev) => ({
                                  ...prev,
                                  interval: event.target.value,
                                }))
                              }
                            />
                            {pingErrors.interval && (
                              <span className="required-message">{pingErrors.interval}</span>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {testType === "dns" && (
                    <>
                      <label className="col-form-label">
                        {localizationStrings?.domainName}
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="www.example.com"
                        value={dnsDomain}
                        onChange={(event) => setDnsDomain(event.target.value)}
                        disabled={isRunning}
                      />
                    </>
                  )}

                  {testType === "traceroute" && (
                    <>
                      <label className="col-form-label">
                        {localizationStrings?.ipAddress} {localizationStrings?.or}{" "}
                        {localizationStrings?.hostName}
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="host"
                        value={traceParams.host}
                        onChange={(event) =>
                          setTraceParams((prev) => ({
                            ...prev,
                            host: event.target.value,
                          }))
                        }
                        placeholder="8.8.8.8 or example.com"
                        disabled={isRunning}
                      />
                      <p className="ts-advanced-toggle">
                        {localizationStrings?.traceDefault}
                        {" · "}
                        <button
                          type="button"
                          onClick={() => setShowAdvanced((open) => !open)}
                        >
                          {localizationStrings?.advancedSettings}
                        </button>
                      </p>
                      {showAdvanced && (
                        <div className="ts-form-grid">
                          <div>
                            <label className="col-form-label">
                              {localizationStrings?.count} (1–10)
                            </label>
                            <input
                              className="form-control"
                              value={traceParams.count}
                              onChange={(event) =>
                                setTraceParams((prev) => ({
                                  ...prev,
                                  count: event.target.value,
                                }))
                              }
                            />
                            {traceErrors.count && (
                              <span className="required-message">{traceErrors.count}</span>
                            )}
                          </div>
                          <div>
                            <label className="col-form-label">Max TTL (1–255)</label>
                            <input
                              className="form-control"
                              value={traceParams.maxTTL}
                              onChange={(event) =>
                                setTraceParams((prev) => ({
                                  ...prev,
                                  maxTTL: event.target.value,
                                }))
                              }
                            />
                            {traceErrors.maxTTL && (
                              <span className="required-message">{traceErrors.maxTTL}</span>
                            )}
                          </div>
                          <div>
                            <label className="col-form-label">
                              {localizationStrings?.InitTTL} (1–30)
                            </label>
                            <input
                              className="form-control"
                              value={traceParams.initTTL}
                              onChange={(event) =>
                                setTraceParams((prev) => ({
                                  ...prev,
                                  initTTL: event.target.value,
                                }))
                              }
                            />
                            {traceErrors.initTTL && (
                              <span className="required-message">{traceErrors.initTTL}</span>
                            )}
                          </div>
                          <div>
                            <label className="col-form-label">
                              {localizationStrings?.port} (1–65535)
                            </label>
                            <input
                              className="form-control"
                              value={traceParams.port}
                              onChange={(event) =>
                                setTraceParams((prev) => ({
                                  ...prev,
                                  port: event.target.value,
                                }))
                              }
                            />
                            {traceErrors.port && (
                              <span className="required-message">{traceErrors.port}</span>
                            )}
                          </div>
                          <div>
                            <label className="col-form-label">
                              {localizationStrings?.size} (46–1024)
                            </label>
                            <input
                              className="form-control"
                              value={traceParams.size}
                              onChange={(event) =>
                                setTraceParams((prev) => ({
                                  ...prev,
                                  size: event.target.value,
                                }))
                              }
                            />
                            {traceErrors.size && (
                              <span className="required-message">{traceErrors.size}</span>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  <div className="ts-actions">
                    <button
                      type="submit"
                      className="text-btn primary-btn"
                      disabled={!canRun}
                    >
                      {isRunning ? (
                        <>
                          <i className="fa fa-spinner fa-spin" aria-hidden="true"></i>{" "}
                          {localizationStrings?.pending || "Running"}
                        </>
                      ) : (
                        localizationStrings?.runTest
                      )}
                    </button>
                  </div>
                </form>

                <div className={`ts-console is-${runState}`}>
                  <div className="ts-console-head">
                    <span>{consoleTitle || localizationStrings?.testResult}</span>
                    {isRunning && (
                      <i className="fa fa-spinner fa-spin" aria-hidden="true"></i>
                    )}
                  </div>
                  <pre>{consoleText || localizationStrings?.noData}</pre>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Troubleshoot;
